<template>
  <div>
    <p
      v-if="error"
      class="text-danger"
      >{{ errorMsg }}</p
    >

    <div
      v-if="initialLoading"
      key="initialLoading"
      class="animate__animated animate__fadeIn animate_fadeOut animate__fast"
    >
      <busy-spinner
        inner-class="my-5"
        text="Loading Users..."
      ></busy-spinner>
    </div>

    <div
      v-show="!initialLoading"
      key="initialLoadingDone"
    >
      <!-- read-only: just the list -- no add / edit / invite actions -->
      <div
        v-if="!readOnly"
        class="d-flex justify-content-between align-items-end mb-2"
      >
        <div class="special-text">Click on a contact's name to edit their details</div>
        <div>
          <b-button
            variant="sharp-success"
            @click="handleAdd"
          >
            <b-icon
              icon="person-plus"
              class="mr-1"
            ></b-icon>
            Add New Contact
          </b-button>
        </div>
      </div>

      <b-card
        id="vendor-contact-table-card"
        class="rounded-lg animate__animated animate__fadeIn animate__fast"
        :class="noShadow ? '' : 'shadow'"
        no-body
      >
        <div
          role="list"
          :aria-busy="loading"
        >
          <div
            v-for="item in itemList"
            :key="item.id"
            role="listitem"
            class="company-contact-item d-flex align-items-start p-3 gap-4"
          >
            <div class="company-contact-details flex-grow-1 d-flex flex-column gap-2">
              <div class="d-flex flex-wrap align-items-center gap-3">
                <div
                  v-if="isFieldVisible('full_name')"
                  class="font-weight-bold flex-fill"
                >
                  <b-link
                    v-if="!readOnly && item.id !== currentUserInfo.tenant_user_id"
                    @click="handleUpdate(item)"
                    >{{ item.full_name }}</b-link
                  >
                  <span v-else>{{ item.full_name }}</span>
                  <b-badge
                    v-if="item.role_slug === 'external_admin'"
                    variant="soft-warning"
                    class="text-uppercase ml-2"
                  >
                    Admin
                  </b-badge>
                  <base-popover-icon
                    v-if="item.manual_block"
                    icon="exclamation-circle-fill"
                    icon-variant="danger"
                    title="Account On Hold"
                    class="ml-2"
                  >
                    This user's account has been placed on hold by Supplios. Please contact Supplios support to remove this restriction.
                  </base-popover-icon>
                </div>
                <div v-if="isFieldVisible('email')" class="flex-fill text-right">{{ item.email }}</div>
              </div>

              <div
                class="ml-3 d-flex flex-wrap align-items-center justify-content-between gap-3 text-muted"
              >
                <div class="d-flex gap-3 align-items-center">
                    <div v-if="isFieldVisible('job_title') && item.job_title" class="">{{ item.job_title }}</div>
                    <div v-if="isFieldVisible('work_location') && item.work_location" class="">{{ item.work_location }}</div>
                </div>
                <div v-if="isFieldVisible('portal_access')" class="flex-fill d-flex justify-content-end">
                  <b-badge
                    v-if="item.portalAccessParams"
                    :key="item.portalAccessParams.key"
                    :variant="item.portalAccessParams.variant"
                    class="text-uppercase"
                    >{{ item.portalAccessParams.label }}
                  </b-badge>
                  <!-- <b-badge
                    v-if="item.portal_access === 'full'"
                    variant="soft-success"
                    class="text-uppercase"
                    >Active</b-badge
                  >
                  <b-badge
                    v-else-if="item.portal_access === 'open_invite'"
                    variant="soft-primary"
                    class="text-uppercase"
                    >Open Invite</b-badge
                  >
                  <b-badge
                    v-else-if="item.portal_access === 'expired_invite'"
                    variant="soft-warning"
                    class="text-uppercase"
                    >Expired Invite</b-badge
                  >
                  <b-badge
                    v-else-if="item.portal_access === 'on_hold'"
                    variant="soft-danger"
                    class="text-uppercase"
                    >Deactivated</b-badge
                  >
                  <b-badge
                    v-else-if="item.portal_access === 'invite_ready'"
                    variant="soft-info"
                    class="text-uppercase"
                    >Invite Ready</b-badge
                  >
                  <b-badge
                    v-else-if="item.portal_access === 'none'"
                    variant="soft-secondary"
                    class="text-uppercase"
                    >No Access</b-badge
                  > -->
                </div>
              </div>

              <div
                v-if="isFieldVisible('tenant_roles') || isFieldVisible('portal_access')"
                class="ml-3 d-flex flex-wrap align-items-center gap-4"
              >
                <div
                  v-if="isFieldVisible('tenant_roles') && item.tenant_roles && item.tenant_roles.length"
                  class="d-flex flex-wrap align-items-center gap-2 flex-fill"
                >
                  <!-- <span class="text-muted">Default contact:</span> -->
                  <b-badge
                    v-for="role in item.tenant_roles"
                    :key="role.slug"
                    variant="soft-secondary"
                    class="text-uppercase"
                  >
                    {{ role.name }}
                  </b-badge>
                </div>
              </div>
            </div>

            <!-- hiding all actions for internal users until you have correct endpoints hooked up -->
            <b-button-group
              v-if="!readOnly && isFieldVisible('actions') && !currentUserIsInternal"
              vertical
              class="flex-shrink-0 ml-auto"
              :aria-label="`Actions for ${item.full_name || item.email}`"
            >
              <b-button
                v-if="canSendReminder(item)"
                :disabled="item.id === currentUserInfo.tenant_user_id"
                variant="subtle-primary"
                size="sm"
                class="text-left"
                @click="sendReminder(item)"
              >
                <b-icon
                  icon="envelope"
                  class="mr-2"
                ></b-icon>
                <span class="text-dark">{{ item.portal_access === 'expired_invite' ? 'Renew Invite' : 'Send Invite' }}</span>
              </b-button>
              <b-button
                :disabled="item.id === currentUserInfo.tenant_user_id || item.portal_access === 'on_hold'"
                variant="subtle-info"
                size="sm"
                class="text-left"
                @click="removeAccess(item)"
              >
                <b-icon
                  icon="x-circle"
                  class="mr-2"
                ></b-icon>
                <span class="text-dark">Remove Access</span>
              </b-button>
              <b-button
                :disabled="item.id === currentUserInfo.tenant_user_id || item.portal_access === 'on_hold'"
                variant="subtle-danger"
                size="sm"
                class="text-left"
                @click="deactivateUser(item)"
              >
                <b-icon
                  icon="trash"
                  class="mr-2"
                ></b-icon>
                <span class="text-dark">Delete User</span>
              </b-button>
            </b-button-group>
          </div>
        </div>
      </b-card>
    </div>
    <!-- End of area to hide when loading -->

    <!-- FOR EXTERNAL USERS USING THIS COMPONENT -->
    <company-portal-user-form
      v-if="!currentUserIsInternal"
      ref="companyUserFormExternal"
      @save-complete="handleContactSaveComplete"
    ></company-portal-user-form>

    <!-- FOR INTERNAL USERS USING THIS COMPONENT -->
    <contact-create-update-modal
      v-if="currentUserIsInternal"
      ref="contactCreateUpdateModal"
      :new-data="{company: companyId}"
      hide-company-input
      modal-title="Add new contact"
      :redirect-after-create="false"
      hide-save-redirect
      save-close-html="Save"
      :success-message-time="0"
      @create-complete="handleContactSaveComplete" 
      @update-complete="handleContactSaveComplete"
     ></contact-create-update-modal>

    <b-modal
      v-model="messageModal"
      centered
      size="sm"
      hide-footer
      hide-header
    >
      <h5 class="text-center mb-0">{{ message }}</h5>
    </b-modal>
  </div>
</template>

<script>
  import { suppliosApi } from '../../js/api-service';
  //import BaseTablePagination from '../BaseTablePagination';
  //import ContactCreateUpdateModal from '../ContactCreateUpdateModal';  // using this only for updates, not creates
  //import isEqual from 'lodash/isEqual';
  import { getPortalAccessParams } from '../tenant_users/tenantUserUtils';
  import { currentUserInfoMixin, tenantInfoMixin } from '../../js/custom-vue-mixins';

  export default {
    name: 'CompanyContactsManager',
    components: {
      //BaseTablePagination,
      //ContactCreateUpdateModal,
    },
    mixins: [currentUserInfoMixin, tenantInfoMixin],
    props: {
      companyId: {
        // if you pass in the company ID, it will only return contacts for that company
        // this allows you to re-use this component for Company Contact List also!
        type: String,
        required: true,
      },
      readOnly: {
        // if true, the component will be in read-only mode -- just the list, no add / edit / invite / remove actions
        type: Boolean,
        default: false,
      },
      hideFields: {
        // array of strings with field keys to HIDE (if they were to be shown normally)
        type: Array,
        required: false,
      },
      noShadow: {
        // removes shadow from list card
        type: Boolean,
        default: false,
      },
    },
    data: function () {
      return {
        errorMsg: 'Error',
        error: false,
        initialLoading: true, // set to false after getting list the first time
        loading: false, // loading signal for fetching list
        loadingInvite: false, // busy flag for sending invite to a contact
        totalRows: null, // total items in list... need to set it after we get items from API, and also after filtering is applied with @filtered event
        itemList: [], // list from API
        messageModal: false, // v-model to control the message modal
        message: '', // message to show in messageModal
      };
    },
    computed: {
      currentUserIsInternal: function () {
        return this.currentUserInfo && this.currentUserInfo.is_internal;
      },
    },
    mounted: function () {
      this.getList();
    },
    methods: {
      // PUBLIC METHOD YOU CAN CALL FROM PARENT TO REFRESH LIST, likely just as $refs.something.refreshList();
      refreshList: function () {
        this.getList();
      },
      handleError: function (error, message) {
        console.log(message);
        console.log(error);
        this.errorMsg = message;
        this.error = true;
      },
      // methods to create/update companies using child CompanyCreateUpdateModal (removed from this component)
      handleUpdate: function (updateObj) {
        if (this.readOnly) return;
        if (this.currentUserIsInternal) {
          // it also has .startUpdate, but that's currently the same as .startView and should not be used for now
          this.$refs.contactCreateUpdateModal.startView(updateObj.id);  // this one only takes the ID
        } else {
          this.$refs.companyUserFormExternal.startUpdate(updateObj);  // this one takes the full update object
        }
      },
      handleAdd: function () {
        // only used if list is empty and user clicks on 'add one' button
        //this.$refs.contactModal.startCreate();
        if (this.readOnly) return;
        if (this.currentUserIsInternal) {
          this.$refs.contactCreateUpdateModal.startCreate();
        } else {
          this.$refs.companyUserFormExternal.startCreate();
        }
      },
      getList: function () {
        this.loading = true;
        return suppliosApi.companyProfileData
          .list_contacts(this.companyId)
          .then((response) => {
            //this.totalRows = response.data.count;
            this.itemList = Array.isArray(response.data) ? response.data.map(item => {
              return {
                ...item,
                portalAccessParams: getPortalAccessParams(item.portal_access),
              };
            }) : [];
            //this.$emit('list-loaded', this.totalRows);  // emit total count to parent
            console.log('Got itemList from getList function');
            console.log(this.itemList);
            return true;
          })
          .catch((error) => {
            this.handleError(error, 'Error while loading itemList');
            return false;
          })
          .then((success) => {
            console.log('hello from last then of getList');
            this.initialLoading = false; // set flag to say we already loaded once
            this.loading = false;
            return success;
          });
      },
      handleContactSaveComplete: function () {
        // no need to show a toast message... form will already show a success message
        this.getList(); // refresh the list
      },
      canSendReminder: function (tuObj) {
        // check tuObj.portal_access to see if they have open_invite or expired_invite
        if (this.currentUserIsInternal) {
          return false;  // need to enable correct endpoint first!
        }
        let portal_access = tuObj.portal_access;
        return ['open_invite', 'expired_invite', 'invite_ready'].includes(portal_access);
      },
      sendReminder: function (tuObj) {
        if (!this.canSendReminder(tuObj)) {
          return false;
        }
        this.message = 'Sending Reminder...';
        this.messageModal = true;
        return suppliosApi.portal  // this is currently only available for EXTERNAL users (same company)
          .companyUserReminder(tuObj.id)
          .then(() => {
            this.$toast.success('Reminder sent');
            this.refreshList();
            return true;
          })
          .catch((error) => {
            this.handleError(error, 'Error while sending reminder');
            return false;
          })
          .then((success) => {
            this.messageModal = false;
            return success;
          });
      },
      removeAccess: function (tuObj) {
        if (this.currentUserIsInternal) {
          return false;  // need to enable correct endpoint first!
        }
        let msg = `Are you sure you want to remove login access for ${tuObj.full_name}? Use this if this person is still an employee, but they should no longer have login access to the supplier portal.`;
        return this.$bvModal
          .msgBoxConfirm(msg, {
            title: 'Remove Access?',
            size: 'sm',
            buttonSize: 'sm',
            okVariant: 'danger',
            okTitle: 'Yes, Remove Access',
            cancelTitle: 'No, Cancel',
            footerClass: 'p-2',
            hideHeaderClose: false,
            centered: true,
          })
          .then((value) => {
            // value will be true (OK clicked), false (cancel clicked) or null (some other way of canceling)
            if (value) {
              this.performRemoveAccess(tuObj);
            }
          })
          .catch((err) => this.handleError(err, 'Error while removing access'));
      },
      performRemoveAccess: function (tuObj) {
        this.loading = true;
        return suppliosApi.portal  // this is currently only available for EXTERNAL users (same company)
          .companyUserRemoveAccess(tuObj.id)
          .then(() => {
            this.$toast.success('Access Removed');
            this.getList();
            return true;
          })
          .catch((error) => {
            this.handleError(error, 'Error while removing access');
            return false;
          })
          .then((success) => {
            this.loading = false;
            return success;
          });
      },
      deactivateUser: function (tuObj) {
        if (this.currentUserIsInternal) {
          return false;  // need to enable correct endpoint first!
        }
        let msg = `Are you sure you want to permanently delete ${tuObj.full_name}?  Only use this if the user is no longer part of your company.`;
        return this.$bvModal
          .msgBoxConfirm(msg, {
            title: 'Delete User?',
            size: 'sm',
            buttonSize: 'sm',
            okVariant: 'danger',
            okTitle: 'Yes, Delete',
            cancelTitle: 'No, Cancel',
            footerClass: 'p-2',
            hideHeaderClose: false,
            centered: true,
          })
          .then((value) => {
            // value will be true (OK clicked), false (cancel clicked) or null (some other way of canceling)
            if (value) {
              this.performDeactivateUser(tuObj);
            }
          })
          .catch((err) => this.handleError(err, 'Error while deleting user'));
      },
      performDeactivateUser: function (tuObj) {
        this.loading = true;
        return suppliosApi.portal  // this is currently only available for EXTERNAL users (same company)
          .companyUserDeactivate(tuObj.id)
          .then(() => {
            this.$toast.success('Deleted User');
            this.getList();
            return true;
          })
          .catch((error) => {
            this.handleError(error, 'Error while deactivating user');
            return false;
          })
          .then((success) => {
            this.loading = false;
            return success;
          });
      },
      isFieldVisible(field) {
        return !this.hideFields || !this.hideFields.includes(field);
      },
    },
  };
</script>

<style scoped>
  .company-contact-item + .company-contact-item {
    border-top: 1px solid var(--gray-200);
  }

  .company-contact-details {
    min-width: 0;
    overflow-wrap: anywhere;
  }
</style>
